import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { leadsApi, type OrganicLeadConversionsReport } from "@/api/leads";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatLeadSourceLabel } from "@/lib/leadSources";

type Props = {
  from: string;
  to: string;
  hospitalId?: string;
};

function fmtDay(isoDay: string): string {
  const [y, m, d] = isoDay.split("-").map(Number);
  if (!y || !m || !d) return isoDay;
  try {
    return new Date(y, m - 1, d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return isoDay;
  }
}

function SummaryCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border bg-white p-4 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{value}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export function OrganicLeadConversionsTab({ from, to, hospitalId }: Props) {
  const params = useMemo(() => {
    const p: Record<string, string> = { from, to };
    if (hospitalId) p.hospitalId = hospitalId;
    return p;
  }, [from, to, hospitalId]);

  const query = useQuery({
    queryKey: ["organic-lead-conversions", params],
    queryFn: async () =>
      (await leadsApi.getOrganicConversionsReport(params)).data.data as OrganicLeadConversionsReport,
    enabled: !!from && !!to,
  });

  const data = query.data;
  const sourceLabels = (data?.sources ?? ["INSTAGRAM_ORGANIC", "WEBSITE"])
    .map((s) => formatLeadSourceLabel(s))
    .join(", ");

  return (
    <div className="space-y-4">
      <Card className="border-slate-200/80 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Organic lead conversions</CardTitle>
          <p className="text-sm text-muted-foreground">
            Sources: {sourceLabels}. Instagram Organic excludes leads whose primary phone has
            more than 14 digits (placeholder / non-mobile rows). Leads are counted on{" "}
            <span className="font-medium text-slate-700">lead created date</span> (IST). OPD visited
            uses{" "}
            <span className="font-medium text-slate-700">OPD consultation bill date</span>. IPD uses{" "}
            <span className="font-medium text-slate-700">discharge date</span> and revenue-track IPD
            bill net amount. Sales person is the effective CRM owner on the matched lead.
          </p>
        </CardHeader>
      </Card>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Loading organic conversions…</p>
      ) : query.isError ? (
        <p className="text-sm text-destructive py-8 text-center">Could not load organic conversions.</p>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard label="Leads created" value={String(data.totals.leadsCreated)} />
            <SummaryCard label="OPD visited (billed)" value={String(data.totals.opdVisited)} />
            <SummaryCard label="IPD discharged" value={String(data.totals.ipdDischarged)} />
            <SummaryCard
              label="IPD bill amount"
              value={`₹${Math.round(data.totals.ipdBillAmount).toLocaleString("en-IN")}`}
              hint="Revenue-track IPD net at discharge"
            />
          </div>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Day × sales person</CardTitle>
              <p className="text-xs text-muted-foreground">
                {data.from} → {data.to} · {data.rows.length} row(s)
              </p>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {data.rows.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No organic leads or conversions in this period.
                </p>
              ) : (
                <table className="w-full min-w-[720px] text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-3 font-medium">Day (IST)</th>
                      <th className="py-2 pr-3 font-medium">Sales person</th>
                      <th className="py-2 pr-3 font-medium text-right">Leads</th>
                      <th className="py-2 pr-3 font-medium text-right">OPD visited</th>
                      <th className="py-2 pr-3 font-medium text-right">IPD discharge</th>
                      <th className="py-2 font-medium text-right">IPD bill (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((row) => (
                      <tr key={`${row.day}-${row.staffId}`} className="border-b last:border-0">
                        <td className="py-2 pr-3 whitespace-nowrap text-slate-800">{fmtDay(row.day)}</td>
                        <td className="py-2 pr-3 text-slate-800">{row.staffName}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{row.leadsCreated}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{row.opdVisited}</td>
                        <td className="py-2 pr-3 text-right tabular-nums">{row.ipdDischarged}</td>
                        <td className="py-2 text-right tabular-nums">
                          {row.ipdBillAmount > 0
                            ? Math.round(row.ipdBillAmount).toLocaleString("en-IN")
                            : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t font-semibold text-slate-900">
                      <td className="py-2 pr-3" colSpan={2}>
                        Total
                      </td>
                      <td className="py-2 pr-3 text-right tabular-nums">{data.totals.leadsCreated}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{data.totals.opdVisited}</td>
                      <td className="py-2 pr-3 text-right tabular-nums">{data.totals.ipdDischarged}</td>
                      <td className="py-2 text-right tabular-nums">
                        {Math.round(data.totals.ipdBillAmount).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
